import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { NavBar, Footer } from '../../components/redesign/shared.jsx';
import SEO from '../../components/SEO.jsx';
import blog1 from '../../assets/blog1.webp';
import blog2 from '../../assets/blog2.webp';
import blog3 from '../../assets/blog3.webp';
import blog4 from '../../assets/blog4.webp';
import llmWikiHero from '../../assets/llm-wiki-hero.png';

// Real posts, most recent first
const POSTS = [
  {
    slug: '/blog/suitecrm-carbon-credit-approval-workflow',
    category: 'Engineering',
    title: 'Carbon Credit Approval Workflows in SuiteCRM: A Technical Guide',
    excerpt: 'How to architect a methodology-gated project approval pipeline in SuiteCRM — intake, reviewer routing, offset volume computation, and an audit trail that holds up in a verification review.',
    image: '/blog-carbon-credit-registry-hero.webp',
    date: 'Apr 23, 2026',
    readTime: '13 min',
  },
  {
    slug: '/blog/llm-wiki-crm',
    category: 'AI & Automation',
    title: "Your CRM Is a Folder of Markdown Files — Here's How It Works",
    excerpt: "What if your CRM was just markdown files maintained by an LLM? No forms, no fields, no data entry. We built it, tested it, and here's what happened.",
    image: llmWikiHero,
    date: 'Apr 10, 2026',
    readTime: '12 min',
  },
  {
    slug: '/blog/suitecrm-8-dockerized-environment',
    category: 'Engineering',
    title: 'SuiteCRM 8 Dockerized: Production-Aligned Environment',
    excerpt: 'A practical setup for SuiteCRM 8 Docker environments with persistent volumes, environment-driven installation, and safe validation workflows.',
    image: '/blog-suitecrm8-dockerized-hero.svg',
    date: 'Mar 3, 2026',
    readTime: '8 min',
  },
  {
    slug: '/blog/openclaw-suitecrm-integration',
    category: 'AI & Automation',
    title: 'OpenClaw Integration: Secure AI Agents in SuiteCRM',
    excerpt: 'A secure, self-hosted integration blueprint for OpenClaw + SuiteCRM with strict boundaries, API-scoped permissions, and event-driven automation.',
    image: '/openclaw-suitecrm-hero.svg',
    date: 'Feb 13, 2026',
    readTime: '12 min',
  },
  {
    slug: '/blog/donor-retention-suitecrm',
    category: 'Operations',
    title: '10 Strategies to Boost Donor Retention with SuiteCRM',
    excerpt: 'Turn occasional donors into lifelong advocates with these proven strategies and enterprise engagement tools.',
    image: blog4,
    date: 'Apr 14, 2025',
    readTime: '4 min',
  },
  {
    slug: '/blog/modernizing-business-operations',
    category: 'AI & Automation',
    title: 'Modernizing Operations: Gen AI + Automation Workflows',
    excerpt: 'Learn how Generative AI and workflow automation transform business operations through RAG, agent orchestration, and privacy-first self-hosting.',
    image: blog2,
    date: 'Jan 15, 2025',
    readTime: '6 min',
  },
  {
    slug: '/blog/crm-process-not-software',
    category: 'Strategy',
    title: 'Why CRM Is a Process, Not Just Software',
    excerpt: 'Learn why CRM is fundamentally a process and discover why open-source architecture is the perfect starting point for scalable operations.',
    image: blog3,
    date: 'Dec 4, 2024',
    readTime: '7 min',
  },
  {
    slug: '/blog/suitecrm-ai-age',
    category: 'Strategy',
    title: 'Why Choose SuiteCRM in the Age of AI?',
    excerpt: 'Discover how SuiteCRM remains relevant in the AI era with its adaptability, integration potential, and cost-effectiveness for enterprise systems.',
    image: blog1,
    date: 'Nov 27, 2024',
    readTime: '5 min',
  },
];

const TOPICS = ['All', 'Strategy', 'Engineering', 'AI & Automation', 'Operations'];

export function BlogA({ onNav }) {
  const [activeTopic, setActiveTopic] = useState('All');
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState(null);

  const filtered = useMemo(
    () => (activeTopic === 'All' ? POSTS : POSTS.filter((p) => p.category === activeTopic)),
    [activeTopic],
  );

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    try {
      const params = new URLSearchParams({ email: newsletterEmail, source: 'resources_hub' });
      const url = `https://n8n.aakeeocrm.com/webhook/5fed7f08-2230-4d07-a78b-7db4c26c0246?${params.toString()}`;
      await fetch(url, { method: 'GET', mode: 'no-cors' });
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'newsletter_signup', {
          event_category: 'lead_capture',
          event_label: 'resources_hub',
        });
      }
      setNewsletterStatus('success');
      setNewsletterEmail('');
    } catch (err) {
      setNewsletterStatus('error');
    }
    setTimeout(() => setNewsletterStatus(null), 5000);
  };

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="pageA">
      <SEO
        title="Resources — Field notes on open-source CRM | Aakeeo"
        description="Playbooks, case studies and technical notes on SuiteCRM, CRM integration, AI workflow automation and self-hosted infrastructure from the Aakeeo team."
        keywords={['open source CRM blog', 'SuiteCRM resources', 'CRM playbook', 'AI CRM automation', 'self-hosted CRM guides']}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Blog',
          name: 'Aakeeo Resources',
          url: 'https://www.aakeeo.com/resources',
          description: 'Field notes from twelve years of shipping CRMs — playbooks, case studies and technical notes.',
          publisher: {
            '@type': 'Organization',
            name: 'Aakeeo Technologies',
            logo: { '@type': 'ImageObject', url: 'https://www.aakeeo.com/logo.png' },
          },
        }}
      />
      <NavBar page="blog" onNav={onNav} />

      <main>

      <section className="blogA-hero">
        <span className="eyebrowA">Resources</span>
        <h1>
          Field notes from <em>twelve years</em> of shipping CRMs that people actually&nbsp;use.
        </h1>
        <p className="blogA-sub">
          Playbooks, case studies and technical notes for founders, IT leaders and RevOps teams choosing open-source CRM.
        </p>
        <div className="blogA-topics">
          {TOPICS.map((t) => (
            <button
              key={t}
              className={'blogA-topic' + (activeTopic === t ? ' is-active' : '')}
              onClick={() => setActiveTopic(t)}
            >
              {t}
            </button>
          ))}
        </div>
      </section>

      {featured && (
        <section className="blogA-feat">
          <Link to={featured.slug} className="blogA-feat-img">
            <img src={featured.image} alt={featured.title} />
          </Link>
          <div className="blogA-feat-copy">
            <span className="blogA-meta">{featured.category} · {featured.date} · {featured.readTime} read</span>
            <h2>
              <Link to={featured.slug}>{featured.title}</Link>
            </h2>
            <p>{featured.excerpt}</p>
            <Link to={featured.slug} className="blogA-read">Read the article →</Link>
          </div>
        </section>
      )}

      <section className="blogA-grid">
        <h3 className="blogA-grid-h">
          {activeTopic === 'All' ? 'Latest' : activeTopic} · {rest.length} {rest.length === 1 ? 'post' : 'posts'}
        </h3>
        {rest.length === 0 ? (
          <p className="blogA-empty">Nothing else under this topic yet — check back soon.</p>
        ) : (
          <div className="blogA-list">
            {rest.map((p) => (
              <article key={p.slug} className="blogA-item">
                <Link to={p.slug} className="blogA-item-img">
                  <img src={p.image} alt={p.title} />
                </Link>
                <span className="blogA-meta">{p.category} · {p.date} · {p.readTime}</span>
                <h3>
                  <Link to={p.slug}>{p.title}</Link>
                </h3>
                <p>{p.excerpt}</p>
                <Link to={p.slug} className="blogA-read">Read article →</Link>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="blogA-news">
        <div>
          <span className="eyebrowA">The Owned CRM Newsletter</span>
          <h2>One essay a month. For teams done renting their&nbsp;CRM.</h2>
          <p>Implementation playbooks, migration case studies, and the occasional rant about per-seat pricing. No spam — unsubscribe in one click.</p>
        </div>
        <form className="blogA-form" onSubmit={handleNewsletter}>
          <input
            type="email"
            placeholder="you@company.com"
            value={newsletterEmail}
            onChange={(e) => setNewsletterEmail(e.target.value)}
            required
            aria-label="Email address"
          />
          <button type="submit" className="btnA-primary">Subscribe</button>
          {newsletterStatus === 'success' && (
            <span className="blogA-form-note" style={{ color: '#2a8a6a' }}>
              Check your inbox — you&apos;re on the list.
            </span>
          )}
          {newsletterStatus === 'error' && (
            <span className="blogA-form-note" style={{ color: 'var(--accent-ink)' }}>
              Something went wrong. Try again in a moment.
            </span>
          )}
          {!newsletterStatus && (
            <span className="blogA-form-note">For RevOps &amp; IT leaders rethinking the stack.</span>
          )}
        </form>
      </section>

      </main>

      <Footer onNav={onNav} />
    </div>
  );
}
